import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import { generateQuartersForYear } from '../../utils/periodUtil';
import {
  useGetAdminAllPEARsQuery,
  useGetDirectReportPEARsQuery,
  useApprovePEARReviewMutation,
  useUpdatePEARReviewMutation,
  PEARReview,
} from '../../redux/pearReviewApi';
// toast removed - react-toastify v8 incompatible with React 19

// Grade chip colors
const gradeColors: Record<string, string> = {
  A: '#4caf50',
  B: '#8bc34a',
  C: '#ff9800',
  D: '#f44336',
};

const AdminPEARReview: React.FC = () => {
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.roles?.includes('admin');
  const quarterOptions = [...generateQuartersForYear(2025), ...generateQuartersForYear(2026)];

  // Use different API based on role
  const {
    data: adminReviews,
    isLoading: isLoadingAdmin,
    refetch: refetchAdmin,
  } = useGetAdminAllPEARsQuery(undefined, {
    skip: !isAdmin,
  });

  const {
    data: managerReviews,
    isLoading: isLoadingManager,
    refetch: refetchManager,
  } = useGetDirectReportPEARsQuery(undefined, {
    skip: isAdmin, // Skip if admin (they use the admin endpoint)
  });

  // Combine based on role
  const allReviews = isAdmin ? adminReviews : managerReviews;
  const isLoading = isAdmin ? isLoadingAdmin : isLoadingManager;
  const refetch = isAdmin ? refetchAdmin : refetchManager;

  // Mutations
  const [approvePEAR, { isLoading: isApproving }] = useApprovePEARReviewMutation();
  const [updatePEAR, { isLoading: isUpdating }] = useUpdatePEARReviewMutation();

  // State
  const [periodFilter, setPeriodFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('submitted');
  const [selectedReview, setSelectedReview] = useState<PEARReview | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);

  // Filter reviews
  const filteredReviews = allReviews?.filter((review) => {
    const matchesPeriod = periodFilter === 'all' || review.reviewPeriod === periodFilter;
    const matchesStatus = statusFilter === 'all' || review.status === statusFilter;
    return matchesPeriod && matchesStatus;
  });

  // Handlers
  const handleView = (review: PEARReview) => {
    router.push(`/reviews/pear/${review.id}?from=admin`);
  };

  const handleOpenApprovalDialog = (review: PEARReview) => {
    setSelectedReview(review);
    setApprovalDialogOpen(true);
  };

  const handleCloseApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setSelectedReview(null);
  };

  const handleApprove = async () => {
    if (!selectedReview) return;

    try {
      await approvePEAR({
        id: selectedReview.id,
      }).unwrap();
      handleCloseApprovalDialog();
      refetch();
    } catch (error) {
      console.error('Failed to approve PEAR Review:', error);
    }
  };

  const handleReject = async (review: PEARReview) => {
    try {
      await updatePEAR({
        id: review.id,
        body: { status: 'rejected' },
      }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to reject PEAR Review:', error);
    }
  };

  // Summary statistics
  const stats = {
    total: allReviews?.length || 0,
    submitted: allReviews?.filter((r) => r.status === 'submitted').length || 0,
    approved: allReviews?.filter((r) => r.status === 'approved').length || 0,
    averageScore:
      allReviews && allReviews.length > 0
        ? allReviews.reduce((sum, r) => sum + (r.overallScore || 0), 0) / allReviews.length
        : 0,
  };

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => router.push('/reviews')}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {isAdmin ? 'PEAR Review Administration' : 'Direct Reports PEAR Reviews'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAdmin 
                  ? 'Review and approve all employee performance evaluations'
                  : 'Review and provide feedback on your team\'s performance evaluations'
                }
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Reviews
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'info.light' }}>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="info.contrastText">
                  {stats.submitted}
                </Typography>
                <Typography variant="body2" color="info.contrastText">
                  Pending Approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="success.contrastText">
                  {stats.approved}
                </Typography>
                <Typography variant="body2" color="success.contrastText">
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold">
                  {stats.averageScore.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Score
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Review Period</InputLabel>
              <Select
                value={periodFilter}
                label="Review Period"
                onChange={(e: SelectChangeEvent) => setPeriodFilter(e.target.value)}
              >
                <MenuItem value="all">All Periods</MenuItem>
                {quarterOptions.map((quarter) => (
                  <MenuItem key={quarter} value={quarter}>
                    {quarter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="submitted">Pending Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* Reviews Table */}
        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredReviews && filteredReviews.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Review Period</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Grade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReviews.map((review) => (
                  <TableRow key={review.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {review.employeeName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{review.designation || 'N/A'}</TableCell>
                    <TableCell>{review.department || 'N/A'}</TableCell>
                    <TableCell>{review.reviewPeriod || 'N/A'}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {review.overallScore?.toFixed(1) || '-'}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {review.finalGrade && (
                        <Chip
                          label={review.finalGrade}
                          size="small"
                          sx={{
                            bgcolor: gradeColors[review.finalGrade] || '#9e9e9e',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={review.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                          size="small"
                          color={
                            review.status === 'approved'
                              ? 'success'
                              : review.status === 'submitted'
                              ? 'info'
                              : review.status === 'rejected'
                              ? 'error'
                              : 'default'
                          }
                        />
                        {review.managerEditedAt && (
                          <Tooltip title={`Manager edited: ${new Date(review.managerEditedAt).toLocaleDateString()}`}>
                            <Chip
                              label="Edited"
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="View PEAR">
                          <IconButton
                            size="small"
                            onClick={() => handleView(review)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit PEAR">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => router.push(`/reviews/pear/${review.id}/edit?from=manager`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {review.status === 'submitted' && isAdmin && (
                          <>
                            <Button
                              size="small"
                              color="success"
                              startIcon={<ApproveIcon />}
                              onClick={() => handleOpenApprovalDialog(review)}
                              disabled={isApproving}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<RejectIcon />}
                              onClick={() => handleReject(review)}
                              disabled={isUpdating}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No PEAR Reviews found matching the selected filters
              </Typography>
            </Box>
          )}
        </TableContainer>

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onClose={handleCloseApprovalDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Approve PEAR Review</DialogTitle>
          <DialogContent>
            {selectedReview && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  You are about to approve the PEAR Review for:
                </Typography>
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Employee
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedReview.employeeName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Review Period
                      </Typography>
                      <Typography variant="body1">
                        {selectedReview.reviewPeriod}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Overall Score
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedReview.overallScore?.toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Grade
                      </Typography>
                      <Chip
                        label={selectedReview.finalGrade}
                        size="small"
                        sx={{
                          bgcolor: gradeColors[selectedReview.finalGrade || 'D'],
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseApprovalDialog}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={isApproving}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default AdminPEARReview;
